// src/components/TimeRecordsTable.tsx

import { useState } from 'react';
import type { TimeRecord, Project } from '@/types';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { formatMinutes } from '@/lib/reports';
import { calculateBilledHoursForRecord } from '@/lib/billing';
import { Download, FileText, Edit, Trash2, Lock } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { getPlanFeatures } from '@/lib/planFeatures';
import { UpgradeModal } from '@/components/UpgradeModal';

interface TimeRecordsTableProps {
  records: TimeRecord[];
  projects: Project[];
  onExportCSV: () => void;
  onExportPDF: () => void;
  onEdit?: (record: TimeRecord) => void;
  onDelete?: (recordId: string) => void;
}

export function TimeRecordsTable({ records, projects, onExportCSV, onExportPDF, onEdit, onDelete }: TimeRecordsTableProps) {
  const { user } = useAuth();
  const features = getPlanFeatures(user);
  const canExportPdf = features.pdfExport;

  const [sortField, setSortField] = useState<'date' | 'project' | 'duration'>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const handleSort = (field: 'date' | 'project' | 'duration') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // PDFボタンクリック時の処理
  const handlePdfClick = () => {
    if (canExportPdf) {
      onExportPDF();
    } else {
      setShowUpgradeModal(true);
    }
  };

  const sortedRecords = [...records].sort((a, b) => {
    let comparison = 0;

    switch (sortField) {
      case 'date':
        comparison = a.clockInTime.toMillis() - b.clockInTime.toMillis();
        break;
      case 'project':
        comparison = (a.project?.name || '').localeCompare(b.project?.name || '', 'ja');
        break;
      case 'duration':
        comparison = (a.totalMinutes || 0) - (b.totalMinutes || 0);
        break;
    }

    return sortDirection === 'asc' ? comparison : -comparison;
  });

  // 実稼働時間と実績時間を計算
  const recordsWithBilling = sortedRecords.map((record) => {
    const actualMinutes = record.totalMinutes || 0;
    const project = projects.find(p => p.id === record.projectId);
    
    let billedMinutes = actualMinutes;
    
    if (project && project.billingAdjustmentType === 'per_item') {
      billedMinutes = calculateBilledHoursForRecord(actualMinutes, project) * 60;
    }
    
    return {
      ...record,
      actualMinutes,
      billedMinutes,
      project: project || record.project,
    };
  });

  const totalActualMinutes = recordsWithBilling.reduce((sum, r) => sum + r.actualMinutes, 0);
  const totalBilledMinutes = recordsWithBilling.reduce((sum, r) => sum + r.billedMinutes, 0);
  const totalActualHours = Math.round(totalActualMinutes / 60 * 100) / 100;
  const totalBilledHours = Math.round(totalBilledMinutes / 60 * 100) / 100;

  return (
    <>
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-800">稼働実績詳細</h2>
          <div className="flex gap-3">
            {/* PDFボタン - Freeプランはグレー＋クリックでモーダル */}
            <button
              onClick={handlePdfClick}
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-md flex items-center gap-2 ${
                canExportPdf
                  ? 'bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700'
                  : 'bg-gray-300 text-gray-500 hover:bg-gray-400'
              }`}
            >
              {!canExportPdf ? (
                <Lock className="w-4 h-4" />
              ) : (
                <FileText className="w-4 h-4" />
              )}
              PDFエクスポート
            </button>
            
            {/* CSVボタン */}
            <button
              onClick={onExportCSV}
              className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-medium hover:from-green-600 hover:to-green-700 transition-all duration-200 shadow-md flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              CSVエクスポート
            </button>
          </div>
        </div>

        {/* サマリー */}
        <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg border border-blue-200">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-sm text-gray-600">合計件数</p>
              <p className="text-2xl font-bold text-gray-900">{records.length}件</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">実稼働時間</p>
              <p className="text-2xl font-bold text-gray-900">{totalActualHours}h</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">実績時間</p>
              <p className="text-2xl font-bold text-blue-600">{totalBilledHours}h</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">合計分</p>
              <p className="text-2xl font-bold text-gray-900">{formatMinutes(totalActualMinutes)}</p>
            </div>
          </div>
        </div>

        {/* テーブル */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th
                  onClick={() => handleSort('date')}
                  className="px-4 py-3 text-left font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  日付 {sortField === 'date' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  onClick={() => handleSort('project')}
                  className="px-4 py-3 text-left font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  プロジェクト {sortField === 'project' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">開始</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">終了</th>
                <th
                  onClick={() => handleSort('duration')}
                  className="px-4 py-3 text-right font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  実稼働(h) {sortField === 'duration' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">
                  実績(h)
                </th>
                <th className="px-4 py-3 text-center font-semibold text-gray-700">記録タイプ</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">備考</th>
                {(onEdit || onDelete) && (
                  <th className="px-4 py-3 text-center font-semibold text-gray-700">操作</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {recordsWithBilling.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                    データがありません
                  </td>
                </tr>
              ) : (
                recordsWithBilling.map((record) => (
                  <tr key={record.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap">
                      {format(record.clockInTime.toDate(), 'yyyy/MM/dd (E)', { locale: ja })}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{record.project?.name || '-'}</div>
                      {record.project?.code && (
                        <div className="text-xs text-gray-500">{record.project.code}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {format(record.clockInTime.toDate(), 'HH:mm')}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {record.clockOutTime ? format(record.clockOutTime.toDate(), 'HH:mm') : '-'}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <div className="font-semibold text-gray-900">
                        {(record.actualMinutes / 60).toFixed(2)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <div className="font-semibold text-blue-600">
                        {(record.billedMinutes / 60).toFixed(2)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {(() => {
                        const type = record.recordType || 'clock';
                        const typeConfig = {
                          clock: { label: '打刻', color: 'bg-green-100 text-green-800' },
                          manual: { label: '手動', color: 'bg-blue-100 text-blue-800' },
                          calendar: { label: 'カレンダー', color: 'bg-purple-100 text-purple-800' },
                        };
                        const config = typeConfig[type];
                        return (
                          <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${config.color}`}>
                            {config.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-gray-600 text-xs line-clamp-2">
                        {record.memo || record.notes || '-'}
                      </span>
                    </td>
                    {(onEdit || onDelete) && (
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          {onEdit && (
                            <button
                              onClick={() => onEdit(record)}
                              className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                              title="編集"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                          )}
                          {onDelete && (
                            <button
                              onClick={() => {
                                if (window.confirm('この記録を削除しますか？')) {
                                  onDelete(record.id);
                                }
                              }}
                              className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                              title="削除"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* アップグレードモーダル */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature="PDFエクスポート"
      />
    </>
  );
}
